/**
 * *Change Product* (`equipment-page-bulk-actions`, R5): the asset card's product picker
 * (`IdentifyProductField`, Brand or Generic) over the selection, sent through the identify call so
 * its chunking, idempotency key, signed Undo (overrides included) and guess refresh come with it.
 * A reading on frame 07's rename popover — the frame draws the button, not what opens.
 *
 * The picker is seeded from the FIRST row as shown (never the first ticked), so its ranked guesses
 * and their reason come from what that row's customer wrote. *Not tracked* is not offered on a set:
 * it clears a product with no Undo (ruled 2026-10-14/15).
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { space } from '@repo/design-tokens';
import { IdentifyProductField, TagSwitcher, type IdentifyPickerFetchers } from '@repo/ui';

import { BulkPopoverFrame } from './BulkPopoverFrame';

export type ProductAnswer = 'brand' | 'generic';

export interface EquipmentChangeProductPopoverProps {
  id: string;
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  /** The selected row ids in the order shown. */
  shownIds: readonly number[];
  fetchers: IdentifyPickerFetchers;
  organisationName?: string;
  logContext?: Readonly<Record<string, string | number>>;
  busy: boolean;
  onChange: (referenceId: string, rowIds: readonly number[]) => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
`;

const ANSWERS = [
  { value: 'brand', text: 'Brand' },
  { value: 'generic', text: 'Generic' },
];

export const EquipmentChangeProductPopover: React.FC<EquipmentChangeProductPopoverProps> = ({
  id,
  open,
  onClose,
  anchorRef,
  shownIds,
  fetchers,
  organisationName,
  logContext,
  busy,
  onChange,
}) => {
  const [answer, setAnswer] = useState<ProductAnswer>('brand');
  const [chosen, setChosen] = useState<string | null>(null);
  const n = shownIds.length;
  const seed = shownIds[0];

  // Focus lands in the picker on opening (Block A), not on the Brand / Generic switch that `Popover`
  // would choose as the first focusable. After `Popover`'s own focus move, so this one wins.
  const fieldWrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    setAnswer('brand');
    setChosen(null);
    const timer = window.setTimeout(
      () => fieldWrap.current?.querySelector<HTMLInputElement>('input')?.focus(),
      0,
    );
    return () => window.clearTimeout(timer);
  }, [open]);

  /*
   * The picker remounts its field once its seed arrives (`IdentifyProductField`'s `fieldKey`), which
   * takes the focused input out of the page and drops focus to the body. Where focus was in the
   * field when that happened, it goes to the new input — found in the real-app keyboard check.
   */
  useEffect(() => {
    if (!open) return;
    // Where focus last ENTERED: removing the focused input fires `focusout` but no `focusin`, so
    // this still says "in the field" when the remount drops focus to the body.
    let inField = false;
    const onFocusIn = (event: FocusEvent) => {
      inField = !!fieldWrap.current && fieldWrap.current.contains(event.target as Node);
    };
    const observer = new MutationObserver(() => {
      const lost = document.activeElement === document.body || document.activeElement === null;
      if (inField && lost) fieldWrap.current?.querySelector<HTMLInputElement>('input')?.focus();
    });
    document.addEventListener('focusin', onFocusIn);
    // The wrapper mounts with the open popover; observe once it is there.
    const timer = window.setTimeout(() => {
      if (fieldWrap.current)
        observer.observe(fieldWrap.current, { childList: true, subtree: true });
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('focusin', onFocusIn);
      observer.disconnect();
    };
  }, [open, seed]);

  const take = (value: string | null) => {
    if (!value || busy) return;
    onChange(value, shownIds);
  };

  return (
    <BulkPopoverFrame
      id={id}
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      glyph="industry"
      title={`Change the product of ${n}`}
      primaryLabel={`Change ${n}`}
      onPrimary={() => take(chosen)}
      primaryUnavailable={chosen === null ? 'choose a product' : null}
      busy={busy}
    >
      <Body>
        <TagSwitcher
          label="Product kind"
          options={ANSWERS}
          selectedValue={answer}
          onChange={value => {
            setAnswer(value as ProductAnswer);
            setChosen(null);
          }}
        />
        {seed !== undefined ? (
          <div ref={fieldWrap}>
            <IdentifyProductField
              key={`${seed}-${answer}`}
              assetId={seed}
              answer={answer}
              fetchers={fetchers}
              organisationName={organisationName}
              logContext={logContext}
              label="Product"
              busy={busy}
              value={chosen}
              onChosenChange={value => setChosen(value)}
              onTake={value => take(value)}
            />
          </div>
        ) : null}
      </Body>
    </BulkPopoverFrame>
  );
};

export default EquipmentChangeProductPopover;
